import React, { useEffect, useMemo, useRef, useState } from 'react';
import allCities from '../../config/allCities.json';
import { CityObj } from '../../config/metadata';

const cities: CityObj[] = allCities;
interface LocationSelectorProps {
  label?: string;
  selected: CityObj[];
  onChange: (locations: CityObj[]) => void;
  required?: boolean;
  placeholder?: string;
}

const normalize = (s: string) => s.toLowerCase();

const LocationSelector: React.FC<LocationSelectorProps> = ({
  label,
  selected,
  onChange,
  required = false,
  placeholder = 'Type to search cities or states...'
}) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const suggestions: Array<CityObj> = useMemo(() => {
    const q = normalize(query).trim();
    if (!q) return [];
    
    const matches: CityObj[] = [];
   
    
    for (let i = 0; i < cities.length && matches.length < 50; i += 1) {
      const city = cities[i];
      const cityName = normalize(city.cityName);
      const stateName = normalize(city.stateName);
      
      // Match city name or state name
      //  `${city.cityName}, ${city.stateName}` displaystring
      if ((cityName.includes(q) || stateName.includes(q)) && !selectedSet.has(city)) {
        matches.push(city);
      }
    }
    
    return matches;
  }, [query, selectedSet]);

  const addLocation = (location: CityObj) => {
    const next = Array.from(new Set([...selected, location]));
    onChange(next);
    setQuery('');
    setOpen(false);
    // re-focus for fast entry
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const removeLocation = (location: CityObj) => {
    onChange(selected.filter(l => l.cityId !== location.cityId));
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      
      if (query.trim()) {
        const q = query.trim();
        const city = cities.find(c => {
          const cityName = normalize(c.cityName);
          // const stateName = normalize(city.stateName);
          return (cityName.includes(q)) && !selectedSet.has(c)
        })
        if(city)
          addLocation(city);
      } 
    } else if (e.key === 'Backspace' && !query && selected.length > 0) {
      // Remove last chip when input empty
      removeLocation(selected[selected.length - 1]);
    }
  };

  return (
    <div className="w-full" ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}{required ? ' *' : ''}
        </label>
      )}
      <div
        className={`w-full min-h-[48px] px-4 py-3 border-2 rounded-xl transition-all duration-200 flex flex-wrap items-center gap-2 ${
          open 
            ? 'border-green-500 bg-green-50 shadow-lg ring-4 ring-green-100' 
            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm focus-within:ring-4 focus-within:ring-green-100 focus-within:border-green-500'
        }`}
        onClick={() => inputRef.current?.focus()}
      >
        {selected.map((location) => (
          <span key={location.cityId} className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-green-100 text-green-800 border border-green-200 font-medium">
            {`${location.cityName}, ${location.stateName}`}
            <button
              type="button"
              className="ml-2 text-green-700 hover:text-green-900 hover:bg-green-200 rounded-full p-0.5 transition-colors duration-150"
              onClick={() => removeLocation(location)}
              aria-label={`Remove ${location}`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={selected.length === 0 ? placeholder : 'Add another location...'}
          className="flex-1 min-w-[140px] outline-none text-sm text-gray-800 placeholder:text-gray-400 py-1 bg-transparent"
        />
        <svg 
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180 text-green-500' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {open && (suggestions.length > 0 || query.trim()) && (
        <div className="mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200">
          <div className="p-3 border-b border-gray-100 bg-gray-50">
            <div className="text-xs text-gray-500 font-medium">Location Suggestions</div>
          </div>
          <div className="max-h-64 overflow-auto">
            {suggestions.map((suggestion) => (
              <button
                key={`${suggestion.cityId}`}
                type="button"
                onClick={() => addLocation(suggestion)}
                className="w-full text-left px-4 py-3 transition-colors duration-150 text-gray-800 hover:bg-gray-50 hover:text-gray-900 border-b border-gray-50 last:border-b-0"
              >
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="font-medium">{`${suggestion.cityName}, ${suggestion.stateName}`}</span>
                </div>
              </button>
            ))}
            {/* {suggestions.length === 0 && query.trim() && (
              <button
                type="button"
                onClick={() => addLocation(query)}
                className="w-full text-left px-4 py-3 transition-colors duration-150 text-gray-600 hover:bg-green-50 hover:text-green-800 border-b border-gray-50 last:border-b-0"
              >
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span className="font-medium">Add "{query}"</span>
                </div>
              </button>
            )} */}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationSelector;
